import {
  AREAS_SERVED,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  SITE_NAME,
  SITE_URL,
  SOCIAL_PROFILES,
  type Faq,
} from '../data/seo'
import { images, serviceCategories } from '../data/site'

type Schema = Record<string, unknown>

const areaServed = AREAS_SERVED.map((name) => ({
  '@type': 'AdministrativeArea',
  name,
}))

export const organizationSchema: Schema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: 'NexLaw',
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/nexlaw-logo.png`,
    width: 512,
    height: 512,
  },
  description:
    'NexLaw is an AI-native law firm combining AI-powered legal workflows with lawyer review to deliver modern legal services in Ontario and Quebec.',
  email: CONTACT_EMAIL,
  telephone: CONTACT_PHONE,
  areaServed,
  sameAs: SOCIAL_PROFILES,
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: CONTACT_EMAIL,
      telephone: CONTACT_PHONE,
      areaServed: AREAS_SERVED,
      availableLanguage: ['English', 'French'],
    },
  ],
}

export const websiteSchema: Schema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  publisher: { '@id': `${SITE_URL}/#organization` },
  inLanguage: 'en-CA',
}

export const legalServiceSchema: Schema = {
  '@context': 'https://schema.org',
  '@type': 'LegalService',
  '@id': `${SITE_URL}/#legalservice`,
  name: SITE_NAME,
  url: SITE_URL,
  image: images.hero,
  description:
    'Technology-assisted legal delivery for individuals, families, and businesses. AI-powered legal workflows with lawyer review and oversight.',
  email: CONTACT_EMAIL,
  telephone: CONTACT_PHONE,
  areaServed,
  parentOrganization: { '@id': `${SITE_URL}/#organization` },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Legal services',
    itemListElement: serviceCategories.map((category) => ({
      '@type': 'OfferCatalog',
      name: category.title,
      itemListElement: category.items.map((item) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: item },
      })),
    })),
  },
}

/** Address, phone, and hours are placeholders until the firm publishes them. */
export const localBusinessSchema: Schema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#localbusiness`,
  name: SITE_NAME,
  url: SITE_URL,
  image: images.hero,
  email: CONTACT_EMAIL,
  telephone: CONTACT_PHONE,
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Address to be announced',
    addressLocality: 'Toronto',
    addressRegion: 'ON',
    postalCode: 'A0A 0A0',
    addressCountry: 'CA',
  },
  areaServed,
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
  ],
}

export function breadcrumbSchema(
  items: { label: string; path: string }[],
): Schema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: `${SITE_URL}${item.path === '/' ? '' : item.path}`,
    })),
  }
}

export function faqSchema(items: Faq[]): Schema {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  }
}

export function webPageSchema({
  title,
  description,
  path,
}: {
  title: string
  description: string
  path: string
}): Schema {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}${path === '/' ? '/' : path}#webpage`,
    url: `${SITE_URL}${path === '/' ? '' : path}`,
    name: title,
    description,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en-CA',
  }
}

/** Ready for the future /resources content centre. */
export function articleSchema({
  headline,
  description,
  path,
  image,
  datePublished,
  dateModified,
  authorName,
}: {
  headline: string
  description: string
  path: string
  image: string
  datePublished: string
  dateModified?: string
  authorName: string
}): Schema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    image,
    datePublished,
    dateModified: dateModified ?? datePublished,
    mainEntityOfPage: `${SITE_URL}${path}`,
    author: { '@type': 'Person', name: authorName },
    publisher: { '@id': `${SITE_URL}/#organization` },
  }
}
